// Iter 265 — Non-dismissable "fill in your dates" gate. Staff missing a
// birthday or a start date get this instead of the app: no opt-out, no skip
// (super admins included), no route behind it is reachable. Signing out is the
// only other way off this screen.
import { useState } from "react";
import { Cake, Award, Loader2, Save, LogOut } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { MONTHS } from "@/lib/celebrations";

const pad2 = (n) => String(n).padStart(2, "0");
const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

export function CelebrationDatesGate() {
  const { user, checkAuth, logout } = useAuth();
  const [bm, setBm] = useState(() => String(parseInt((user?.birthday || "").split("-")[0], 10) || ""));
  const [bd, setBd] = useState(() => String(parseInt((user?.birthday || "").split("-")[1], 10) || ""));
  const [start, setStart] = useState(() => user?.work_anniversary || "");
  const [busy, setBusy] = useState(false);

  const today = new Date().toISOString().slice(0, 10);
  const ready = bm && bd && /^\d{4}-\d{2}-\d{2}$/.test(start);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/auth/profile", {
        birthday: `${pad2(bm)}-${pad2(bd)}`,
        work_anniversary: start,
      });
      toast.success("Thanks — you're all set.");
      await checkAuth?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save those dates.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/95 p-4"
      data-testid="celebration-dates-gate">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl dark:bg-slate-900">
        <div className="flex items-center gap-2.5">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white">
            <Cake className="h-5 w-5" />
          </span>
          <div>
            <h1 className="font-heading text-lg font-bold text-slate-900 dark:text-slate-100">
              Two dates before you carry on
            </h1>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              We celebrate birthdays and work anniversaries as a team — yours are missing.
            </p>
          </div>
        </div>

        <div className="mt-5 space-y-4">
          <div>
            <Label className="flex items-center gap-1.5 text-xs">
              <Cake className="h-3.5 w-3.5 text-slate-400" /> Your birthday
            </Label>
            <p className="mb-1.5 mt-0.5 text-[11px] text-slate-400 dark:text-slate-500">
              Month and day only — we never ask for the year.
            </p>
            <div className="flex gap-2">
              <Select value={bm} onValueChange={setBm}>
                <SelectTrigger data-testid="gate-birthday-month"><SelectValue placeholder="Month" /></SelectTrigger>
                <SelectContent className="z-[130]">
                  {MONTHS.map((m, i) => (
                    <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={bd} onValueChange={setBd}>
                <SelectTrigger className="w-28" data-testid="gate-birthday-day"><SelectValue placeholder="Day" /></SelectTrigger>
                <SelectContent className="z-[130] max-h-72">
                  {DAYS.map((d) => <SelectItem key={d} value={String(d)}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="flex items-center gap-1.5 text-xs">
              <Award className="h-3.5 w-3.5 text-slate-400" /> The day you started here
            </Label>
            <p className="mb-1.5 mt-0.5 text-[11px] text-slate-400 dark:text-slate-500">
              Full date — the year is what counts the years of service.
            </p>
            <Input type="date" value={start} max={today} min="1980-01-01"
              onChange={(e) => setStart(e.target.value)} data-testid="gate-start-date" />
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between gap-3">
          <button type="button" onClick={logout} data-testid="gate-sign-out"
            className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
          <Button onClick={save} disabled={busy || !ready} data-testid="gate-save" className="gap-1.5">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save & continue
          </Button>
        </div>
      </div>
    </div>
  );
}
